// Liste centralisee des maps RMF supportees par le site.
// L'ordre defini ici est aussi celui d'affichage dans le HUD switcher.

import type { TaskCategory } from '$lib/api/backend-client';

export type MapName =
  | 'office'
  | 'hotel'
  | 'clinic'
  | 'airport_terminal'
  | 'triple_H'
  | 'battle_royale'
  | 'campus';

export type MapDescriptor = {
  readonly name: MapName;
  /** Cle i18n pour le label court affiche dans le HUD et les listes. */
  readonly labelKey: string;
  /** Cle i18n pour la description longue, utilisee dans le MapPicker de /welcome. */
  readonly descriptionKey: string;
  /** True si la map n'est pas dispatchable (GPS coords, fleet upstream absente, etc.). */
  readonly placeholder: boolean;
  /**
   * Nombre de fleet_adapter attendus une fois la chaine RMF complete (phase 4 du readiness).
   * Source : scripts/e2e-rmf-full.sh + rmf-task-dispatch.md § Detail par world. Si /api/fleets
   * retourne moins que ca, la session est degradee (cf bug task #36 upstream Open-RMF) et
   * certains dispatch echoueront. 0 pour les placeholders.
   */
  readonly expectedFleets: number;
  /**
   * Categories de task reellement dispatchables sur ce world. Une fleet peut declarer
   * supporter une categorie au niveau global (cf FLEET_CAPS) sans qu'elle soit jouable sur
   * un world donne (ex : tinyRobot supporte delivery, mais hotel n'expose pas de
   * pickup_dispenser/dropoff_ingestor upstream, donc delivery y est rejete 422). Source :
   * matrice scripts/test-matrix-dispatch.sh + rmf-task-dispatch.md § Pieges connus.
   */
  readonly supportedCategories: readonly TaskCategory[];
};

export const MAPS: readonly MapDescriptor[] = [
  {
    name: 'office',
    labelKey: 'map_office',
    descriptionKey: 'map_office_description',
    placeholder: false,
    expectedFleets: 1,
    supportedCategories: ['patrol', 'delivery']
  },
  {
    name: 'hotel',
    labelKey: 'map_hotel',
    descriptionKey: 'map_hotel_description',
    placeholder: false,
    // Bug upstream task #36 : le bon nombre est 3 mais l'API ne renvoie souvent que 2. On
    // garde la valeur theorique ici, c'est la logique de retry cote front qui absorbe le
    // glitch (cf chantier 3).
    expectedFleets: 3,
    // Pas de delivery sur hotel : pas de dispenser/ingestor dans le building.yaml upstream
    // (cf rmf-task-dispatch.md § Pieges connus #1).
    supportedCategories: ['patrol', 'clean']
  },
  {
    name: 'clinic',
    labelKey: 'map_clinic',
    descriptionKey: 'map_clinic_description',
    placeholder: false,
    expectedFleets: 2,
    // Multi-niveau (L1/L2) mais pas de dispenser/ingestor upstream, pas de cleanerBot.
    supportedCategories: ['patrol']
  },
  {
    name: 'airport_terminal',
    labelKey: 'map_airport_terminal',
    descriptionKey: 'map_airport_terminal_description',
    placeholder: false,
    // tinyRobot + deliveryRobot + cleanerBotA + cleanerBotE (4 fleets actives, caddy
    // read-only est exclu cote dispatch).
    expectedFleets: 4,
    supportedCategories: ['patrol', 'delivery', 'clean']
  },
  {
    name: 'triple_H',
    labelKey: 'map_triple_H',
    descriptionKey: 'map_triple_H_description',
    // Aucune fleet upstream Open-RMF pour ce world : pas de fleet_config.yaml, pas de
    // launch.xml dans rmf_demos. Rendu 3D possible mais aucun dispatch jouable, on traite
    // comme un placeholder visuel.
    placeholder: true,
    expectedFleets: 0,
    supportedCategories: []
  },
  {
    name: 'battle_royale',
    labelKey: 'map_battle_royale',
    descriptionKey: 'map_battle_royale_description',
    placeholder: false,
    expectedFleets: 1,
    // 4 tinyRobot, layout minimal start_X -> goal_X. Pas de dispenser ni cleanerBot.
    supportedCategories: ['patrol']
  },
  {
    name: 'campus',
    labelKey: 'map_campus',
    descriptionKey: 'map_campus_description',
    // GPS WGS84 coords, pas extrude (cf ADR 014).
    placeholder: true,
    expectedFleets: 0,
    supportedCategories: []
  }
];

const MAP_BY_NAME: ReadonlyMap<string, MapDescriptor> = new Map(MAPS.map((m) => [m.name, m]));

export function isValidMapName(value: string): value is MapName {
  return MAP_BY_NAME.has(value);
}

export function getMap(name: string): MapDescriptor | undefined {
  return MAP_BY_NAME.get(name);
}

/**
 * En backend reel (VITE_BACKEND_MOCK=false), le choix du world est verrouille a `hotel`.
 * C'est le world le plus complet de la demo (multi-flottes, multi-niveaux, lifts, doors,
 * dispatch jouable), et ca evite que les visiteurs tombent sur des worlds non-finis
 * (placeholder, fleet_adapter KO upstream). En backend mock, le verrou est leve pour
 * pouvoir tester chaque world independamment.
 *
 * Gate intentionnellement sur `useMockBackend` et PAS sur `import.meta.env.DEV` : on veut
 * que le verrou s'applique aussi en `pnpm dev` quand on branche un vrai backend pour test
 * pre-prod (sinon on serait pas isofunctionel avec le build prod).
 *
 * Consommateurs :
 *   - `/welcome` MapPicker : si non-null, force la pre-selection sur cette valeur et desactive
 *     le clic sur les autres cartes.
 *   - `SettingsMenu` (HUD `/[map]`) : si non-null, la section world du dropdown est masquee.
 *
 * Inline par Vite au build : `useMockBackend` resolu en constante boolean, branche morte
 * tree-shake selon le mode.
 */
const useMockBackend = import.meta.env.VITE_BACKEND_MOCK !== 'false';
export const FORCED_WORLD: MapName | null = useMockBackend ? null : 'hotel';
